import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";

import { BarElement, CategoryScale, Chart, ChartOptions, Legend, LinearScale } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import { Flex, Icon, Tooltip } from "components";

Chart.register(BarElement, CategoryScale, ChartDataLabels, Legend, LinearScale);

export type BarData = {
  label: string;
  value: number;
  color?: string;
};

export type BarChartProps = {
  title?: string;
  subtitle?: string;
  tooltip?: string;
  bars: BarData[];
  formatFunction?: (value: number) => string;
  width?: number;
  height?: number;
  tag?: React.ReactNode;
};

const DEFAULT_COLORS = [
  "#065C45", // green-400
  "#B4A8C8", // purple
  "#ECF86E", // Yellow Rapeseed Primary 500
  "#999", // grey-500
];

const COLORS = {
  black: "#100C06",
  grey: "#999",
};

export const BarChart = ({
  // TODO: speak with design on how to consolidate bar-charts
  title,
  subtitle,
  tooltip,
  bars,
  formatFunction = (value: number) => `${value.toFixed(2)} kg`,
  width = 400,
  height = 350,
  tag,
}: BarChartProps) => {
  const chartRef = useRef<Chart<"bar", number[], string>>(null);

  const options: ChartOptions<"bar"> = {
    plugins: {
      datalabels: {
        formatter: (value: number) => formatFunction(value),
        anchor: "end",
        align: "top",
        offset: 4,
        color: COLORS.black,
        font: {
          size: 16,
          weight: 500,
          family: "dazzed",
        },
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: COLORS.black,
          font: {
            size: 14,
            family: "dazzed",
            weight: 500,
          },
        },
      },
      y: {
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          color: "#e5e5e5",
          drawTicks: false,
        },
        ticks: {
          padding: 16,
          font: {
            size: 14,
            family: "dazzed",
            weight: 500,
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 2,
  };

  const data = {
    labels: bars.map((bar) => bar.label),
    datasets: [
      {
        data: bars.map((bar) => bar.value),
        backgroundColor: bars.map((bar, index) => bar.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]),
        borderRadius: {
          topLeft: 8,
          topRight: 8,
          bottomLeft: 0,
          bottomRight: 0,
        },
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
    ],
  };

  return (
    <>
      {/* Header */}
      <Flex vertical className="margin-bottom-100">
        <Flex align="center" justify="space-between" className="margin-bottom-50">
          <Flex align="center" gap={8}>
            <Tooltip placement="bottomLeft" disabled={tooltip === undefined} title={tooltip}>
              <Flex gap="1rem" align="center">
                <h5>{title}</h5>
                <Icon size="s" iconName="tabler/info-circle" className="icon inline" color="grey" />
              </Flex>
            </Tooltip>
          </Flex>
          {tag}
        </Flex>
        <p className="paragraph-s grey-600-text margin-0 margin-bottom-100">{subtitle}</p>
      </Flex>

      {/* Chart */}
      <div style={{ width: `${width}px`, height: `${height}px` }}>
        <Bar ref={chartRef} options={options} data={data} />
        {/* Hidden elements with classNames for styling reference */}
        <div style={{ display: "none" }}>
          {bars.map((bar, index) => (
            <React.Fragment key={index}>
              <p className="paragraph-m" data-label={bar.label}>
                {bar.label}
              </p>
              <p className="paragrpah-m" data-value={bar.value}>
                {bar.value}
              </p>
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
};
